import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TabNavigation, type TabConfig } from "../../components/TabNavigation";
import { settingsService } from "./services/settingsService";
import type { SettingsState } from "./services/settingsTypes";
import { ChatSettingsTab } from "./tabs/ChatSettingsTab";
import { CommunitySettingsTab } from "./tabs/CommunitySettingsTab";
import { GeneralSettingsTab } from "./tabs/GeneralSettingsTab";
import { JourneySettingsTab } from "./tabs/JourneySettingsTab";
import { OcdProfileSettingsTab } from "./tabs/OcdProfileSettingsTab";
import { VoiceSettingsTab } from "./tabs/VoiceSettingsTab";

const TABS = ['general', 'community', 'journey', 'chat', 'voice', 'ocd-profile'] as const;
type TabId = (typeof TABS)[number];

const TAB_CONFIG: TabConfig<TabId>[] = [
  { id: 'general', label: 'General', icon: '⚙️' },
  { id: 'community', label: 'Community', icon: '💬' },
  { id: 'journey', label: 'Journey', icon: '🧭' },
  { id: 'chat', label: 'Chat', icon: '🗨️' },
  { id: 'voice', label: 'Voice & dictation', icon: '🎙️' },
  { id: 'ocd-profile', label: 'OCD profile', icon: '🧩' },
];

const isTabId = (value: string | null): value is TabId =>
  value !== null && (TABS as readonly string[]).includes(value);

export const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState<SettingsState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // The tab lives in the query string, so a link opens the same tab.
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = isTabId(tabParam) ? tabParam : 'general';

  const setTab = (tab: TabId) => {
    setSearchParams(tab === 'general' ? {} : { tab });
  };

  useEffect(() => {
    const fetchSettings = async () => {
      const settings = await settingsService.getSettings();
      setSettings(settings);
      setIsLoading(false);
    };
    fetchSettings();
  }, []);

  const handleSettingsChange = (change: Partial<SettingsState>) => {
    setSettings((current) => current && { ...current, ...change });
  };

  const renderTab = (settings: SettingsState) => {
    const tabProps = { settings, onSettingsChange: handleSettingsChange };
    switch (activeTab) {
      case 'general': return <GeneralSettingsTab {...tabProps} />;
      case 'community': return <CommunitySettingsTab {...tabProps} />;
      case 'journey': return <JourneySettingsTab {...tabProps} />;
      case 'chat': return <ChatSettingsTab />;
      case 'voice': return <VoiceSettingsTab {...tabProps} />;
      case 'ocd-profile': return <OcdProfileSettingsTab />;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
      <TabNavigation tabs={TAB_CONFIG} activeTab={activeTab} onTabChange={setTab} />
      {isLoading ? (
        <div>Loading...</div>
      ) : settings ? (
        <div className="flex flex-col gap-4">{renderTab(settings)}</div>
      ) : (
        <div>No settings found</div>
      )}
    </div>
  );
};
